import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../lib/api.ts';
import { CenteredSpinner } from '../components/Spinner.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { CompanyStatusBadge } from '../components/StatusBadge.tsx';
import type { CompanyWithCounts } from '../lib/types.ts';

export function AdminReviews() {
  const { data, isLoading } = useQuery({
    queryKey: ['review-queue'],
    queryFn: () =>
      adminApi.get<{ companies: CompanyWithCounts[] }>('/api/companies/review/queue'),
  });

  if (isLoading) return <CenteredSpinner />;
  const companies = data?.companies ?? [];
  const totalPending = companies.reduce(
    (n, c) => n + (c.submission_counts?.pending ?? 0) + (c.submission_counts?.rejected ?? 0),
    0,
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Reviews</h1>
        <p className="mt-1 text-sm text-slate-500">
          {companies.length} compan{companies.length === 1 ? 'y' : 'ies'} awaiting review
          {totalPending > 0 ? ` · ${totalPending} submission${totalPending === 1 ? '' : 's'} to action` : ''}
        </p>
      </div>

      {companies.length === 0 ? (
        <EmptyState
          title="Nothing waiting for review"
          description="Companies that have submitted their questionnaire will appear here."
        />
      ) : (
        <div className="space-y-2">
          {companies.map((c) => {
            const counts = c.submission_counts ?? { pending: 0, rejected: 0, approved: 0, total: 0 };
            const pending = counts.pending + counts.rejected;
            return (
              <Link
                key={c.id}
                to={`/admin/companies/${c.id}`}
                className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4 hover:border-slate-300"
              >
                <div>
                  <div className="font-medium text-slate-900">{c.company_name}</div>
                  <div className="text-sm text-slate-500">
                    {c.contact_name} · {c.email}
                  </div>
                </div>
                <div className="flex items-center gap-3 text-right">
                  <div className="text-xs text-slate-500">
                    <div>
                      <span className="font-semibold text-slate-700">{pending}</span> to review
                      <span className="text-slate-400"> · {counts.approved}/{counts.total} approved</span>
                    </div>
                    <div>Updated {new Date(c.updated_at).toLocaleDateString()}</div>
                  </div>
                  <CompanyStatusBadge status={c.status} />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
